/* eslint-disable react-refresh/only-export-components */
//Disable because Fast refresh only works when a file only exports components
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from 'react';

import { ReportItems } from '../constants';
import { AdsResponseType, SalesRefResponseType } from '../types/apiTypes';
import {
  getAdsData,
  getSalesReferenceData,
  getSelectedRefSqmPrice,
} from '../helpers';
import { ProjectType, setCurrentProjectType } from '../types/dataTypes';
import { TabsType } from '../enums';
import { defaultColumnOptions } from './default';
import { defaultAdsTableColumns } from '@/context/AdsDefault';
import { defaultSoldTableColumns } from '@/context/SoldDefault';
import { getTodayDateISO, subtractYearsFromDate, convertDateToISO } from '@/helpers';

const defaultProject:ProjectType = {
  id: null,
  name: null,
  adsTableData: null,
  soldTableData: null,
  soldSummary: null,
  adsSummary: null,
  marketAds: null,
  indexValues: null,
  adsTableIds: { selected: null, excluded: null },
  soldTableIds: { selected: null, excluded: null },
  adsTableColumns: defaultAdsTableColumns,
  soldTableColumns: defaultSoldTableColumns,
  adsTableReportColumns: defaultAdsTableColumns,
  soldTableReportColumns: defaultSoldTableColumns,
  referencePrice: { sqmPrice: null, sqmPriceSelected: null },
  adsPolygons: null,
  soldPolygons: null,
  marketPolygons: null,
  integration_id: null,
  integration_name: null,
  integration_upload: false,
  indexData: null,
  sale_references_sort: null,
  reportComment: '',
  own_choice_reference_fields: [],
    index_normalized_fields:{
    reference_date_from: convertDateToISO(subtractYearsFromDate(new Date(), 15)),
    reference_date_to: getTodayDateISO()
  },
  ad_references_fields: {
    address_field: false,
    ad_date_field: false,
    advertising_days_field: false,
    build_year_field: false,
    living_space_field: false,
    room_field: false,
    floors_field: false,
    plot_size_field: false,
    ancillary_size_field: false,
    starting_price_field: false,
    starting_price_m2_field: false,
    monthly_charge_field: false,
    ownership_type_field: false,
    real_estate_firm_field: false,
    ad_link_field: false,
  }
};

interface ProjectContextType {
  project: ProjectType;
  selectedTab: string;
  selectedReportItems: { [key: string]: boolean } | null;
  visitedTabs: Set<string>;
  isAdvancedFilterOpen: boolean;
  columnOption: string | number;
  autoSave: boolean;
  showPropertyLines: boolean;
  handleReportItemsToggle: (value: string) => void;
  resetReportItems: () => void;
  setNewProject: (data: setCurrentProjectType) => void;
  setProject: Dispatch<SetStateAction<ProjectType>>;
  resetProject: () => void;
  setIsAdvancedFilterOpen: Dispatch<SetStateAction<boolean>>;
  setSelectedTab: Dispatch<SetStateAction<string>>;
  updateSoldTableData: (data: SalesRefResponseType) => void;
  updateAdsTableData: (data: AdsResponseType) => void;
  toggleSelectedTableIds: (state: TabsType, id: string) => void;
  addExcludedTableIds: (state: TabsType, id: string) => void;
  toggleColumnVisibility: (state: TabsType, id: string) => void;
  setColumnOption: (value: string | number) => void;
  setAutoSave: (value: boolean) => void;
  setShowPropertyLines: (value: boolean) => void;
  toggleReportColumnVisibility: (state: TabsType, id: string) => void;
}

const defaultProjectContext: ProjectContextType = {
  project: defaultProject,
  autoSave: true,
  selectedTab: TabsType.sold,
  selectedReportItems: null,
  visitedTabs: new Set([TabsType.sold]),
  isAdvancedFilterOpen: false,
  columnOption: '',
  handleReportItemsToggle: () => { },
  setNewProject: () => { },
  setProject: () => { },
  resetProject: () => { },
  setIsAdvancedFilterOpen: () => { },
  setSelectedTab: () => { },
  updateSoldTableData: () => { },
  updateAdsTableData: () => { },
  toggleSelectedTableIds: () => { },
  addExcludedTableIds: () => { },
  toggleColumnVisibility: () => { },
  setColumnOption: () => { },
  resetReportItems: () => { },
  setAutoSave: () => { },
  setShowPropertyLines: () => { },
  toggleReportColumnVisibility: () => { }
};

const ProjectContext = createContext<ProjectContextType>(defaultProjectContext);

interface ProjectProviderProps {
  children?: ReactNode;
}

const ProjectProvider: FC<ProjectProviderProps> = ({ children }) => {
  const [project, setProject] = useState<ProjectType>(defaultProject);
  const [columnOption, setColumnOption] = useState<string | number>(
    defaultColumnOptions[0].value
  );
  const [autoSave, setAutoSave] = useState<boolean>(true);
  const [selectedTab, setSelectedTab] = useState<string>(TabsType.sold);
  const [showPropertyLines, setShowPropertyLines] = useState<boolean>(false);
  const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] =
    useState<boolean>(false);
  const [selectedReportItems, setSelectedReportItems] = useState<{
    [key: string]: boolean;
  }>(() =>
    ReportItems.reduce((acc, item) => ({ ...acc, [item.value]: false }), {})
  );
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(new Set([TabsType.sold]));

  const updateSoldTableData = (data: SalesRefResponseType) => {
    const tableData = getSalesReferenceData(data.sales);
    
    const { average_price_today_sqm } = data.summary;

    const sqmPriceSelected = getSelectedRefSqmPrice(
      tableData,
      project.soldTableIds.selected
    );

    setProject((prevState) => ({
      ...prevState,
      soldTableData: tableData,
      soldSummary: data.summary,
      referencePrice: { sqmPrice: average_price_today_sqm, sqmPriceSelected },
    }));
  };

  const updateAdsTableData = (data: AdsResponseType) => {
    const tableData = getAdsData(data.ads);

    setProject((prevState) => ({
      ...prevState,
      adsTableData: tableData,
      adsSummary: data.summary,
    }));
  };

  const handleReportItemsToggle = (value: string) => {
    setSelectedReportItems((prev) => ({
      ...prev,
      [value]: !prev[value],
    }));
  };

  const resetReportItems = () => {
    setSelectedReportItems(ReportItems.reduce((acc, item) => ({ ...acc, [item.value]: false }), {}));
  };

  const toggleSelectedTableIds = (state: TabsType, id: string) => {
    switch (state) {
      case TabsType.ads:
        setProject((prevState) => {
          const newSelected = new Set(prevState.adsTableIds.selected);
          if (newSelected.has(id)) {
            newSelected.delete(id);
          } else {
            newSelected.add(id);
          }

          return {
            ...prevState,
            adsTableIds: {
              ...prevState.adsTableIds,
              selected: newSelected,
            },
          };
        });

        break;
      case TabsType.sold:
        setProject((prevState) => {
          const newSelected = new Set(prevState.soldTableIds.selected);
          if (newSelected.has(id)) {
            newSelected.delete(id);
          } else {
            newSelected.add(id);
          }

          let sqmPriceSelected;
          if (project.soldTableData) {
            sqmPriceSelected = getSelectedRefSqmPrice(
              project.soldTableData,
              newSelected
            );
          }

          return {
            ...prevState,
            soldTableIds: {
              ...prevState.soldTableIds,
              selected: newSelected,
            },
            referencePrice: {
              ...prevState.referencePrice,
              sqmPriceSelected: sqmPriceSelected || null,
            },
          };
        });

        break;
    }
  };


  const addExcludedTableIds = (state: TabsType, id: string) => {
    // //REMOVE FROM SELECTED
    if (state == TabsType.ads) {
      const updateSelectedIdsAds = new Set();
      project.adsTableIds.selected?.forEach((selectedId) => {
        if (!project.adsTableIds.excluded?.has(selectedId)) {
          updateSelectedIdsAds.add(selectedId);
        }
      });

      if (updateSelectedIdsAds.has(id)) {
        updateSelectedIdsAds.delete(id);
      }

      setProject((prevState) => {
        return {
          ...prevState,
          adsTableIds: {
            ...prevState.adsTableIds,
            excluded: new Set(prevState.adsTableIds.excluded).add(id),
            selected: updateSelectedIdsAds
          },
        };
      });

    } else if (state == TabsType.sold) {

      const updateSelectedIdsSold = new Set();
      project.soldTableIds.selected?.forEach((selectedId) => {
        if (!project.soldTableIds.excluded?.has(selectedId)) {
          updateSelectedIdsSold.add(selectedId);
        }
      });

      if (updateSelectedIdsSold.has(id)) {
        updateSelectedIdsSold.delete(id);
      }

      setProject((prevState) => {
        return {
          ...prevState,
          soldTableIds: {
            ...prevState.soldTableIds,
            excluded: new Set(prevState.soldTableIds.excluded).add(id),
            selected: updateSelectedIdsSold
          },
        };
      });
    }
  };

  const toggleColumnVisibility = (state: TabsType, id: string) => {
    switch (state) {
      case TabsType.sold:
        setProject((prevState) => ({
          ...prevState,
          soldTableColumns: prevState.soldTableColumns.map((column) =>
            column.id === id
              ? { ...column, isVisible: !column.isVisible }
              : column
          ),
          soldTableReportColumns: prevState.soldTableReportColumns.map((column) =>
            column.id === id
              ? { ...column, isVisible: !column.isVisible }
              : column
          ),
        }));
        break;

      case TabsType.ads:
        setProject((prevState) => ({
          ...prevState,
          adsTableColumns: prevState.adsTableColumns.map((column) =>
            column.id === id
              ? { ...column, isVisible: !column.isVisible }
              : column
          ),
          adsTableReportColumns: prevState.adsTableReportColumns.map((column) =>
            column.id === id
              ? { ...column, isVisible: !column.isVisible }
              : column
          ),
        }));

        break;
    }
  };

  const toggleReportColumnVisibility = (state: TabsType, id: string) => {
    switch (state) {
      case TabsType.sold:
        setProject((prevState) => ({
          ...prevState,
          soldTableReportColumns: prevState.soldTableReportColumns.map((column) =>
            column.id === id
              ? { ...column, isVisible: !column.isVisible }
              : column
          ),
        }));
        break;

      case TabsType.ads:
        setProject((prevState) => ({
          ...prevState,
          adsTableReportColumns: prevState.adsTableReportColumns.map((column) =>
            column.id === id
              ? { ...column, isVisible: !column.isVisible }
              : column
          ),
        }));

        break;
    }
  };

  const setNewProject = ({
    id,
    name,
    excludedSoldIds,
    selectedSoldIds,
    excludedAdsIds,
    selectedAdsIds,
    indexValues,
    integration_name,
    adsPolygons,
    soldPolygons,
    marketPolygons,
    integration_id,
    integration_upload,
    sale_references_sort,
    sale_references_category,
    ad_references_fields,
    index_normalized_fields
  }: setCurrentProjectType) => {
    const tmpSalesReferencesField = project.soldTableColumns;
    const tmpAdReferencesField = project.adsTableColumns;

    setProject((prevProject) => ({
      ...prevProject,
      id,
      name,
      integration_name,
      integration_id,
      integration_upload,
      indexValues,
      adsPolygons,
      soldPolygons,
      sale_references_sort : sale_references_sort,
      marketPolygons,
      sale_references_category,
      own_choice_reference_fields: tmpSalesReferencesField,
      ad_references_fields,
      index_normalized_fields: index_normalized_fields || {
        reference_date_from: convertDateToISO(subtractYearsFromDate(new Date(), 15)),
        reference_date_to: getTodayDateISO()
      },
      adsTableIds: {
        selected: new Set(selectedAdsIds),
        excluded: new Set(excludedAdsIds),
      },
      soldTableIds: {
        selected: new Set(selectedSoldIds),
        excluded: new Set(excludedSoldIds),
      },
      adsTableColumns: tmpAdReferencesField
    }));

    if (sale_references_category) {
      setColumnOption(sale_references_category);
    } else {
      setColumnOption(defaultColumnOptions[0].value);
    }
    setSelectedReportItems(() =>
      ReportItems.reduce((acc, item) => ({ ...acc, [item.value]: false }), {})
    );
    setVisitedTabs(new Set([TabsType.sold]));
  };

  const resetProject = () => {
    setProject(defaultProject);
    setVisitedTabs(new Set([TabsType.sold]));
  };

  // Override setSelectedTab to track visited tabs
  const setSelectedTabWithTracking = (tab: string) => {
    setSelectedTab(tab);
    setVisitedTabs(prev => new Set(prev).add(tab));
  };

  return (
    <ProjectContext.Provider
      value={{
        project,
        selectedTab,
        isAdvancedFilterOpen,
        selectedReportItems,
        visitedTabs,
        columnOption,
        autoSave,
        showPropertyLines,
        handleReportItemsToggle,
        setIsAdvancedFilterOpen,
        setNewProject,
        setSelectedTab: setSelectedTabWithTracking,
        setProject,
        resetProject,
        updateSoldTableData,
        updateAdsTableData,
        toggleSelectedTableIds,
        addExcludedTableIds,
        toggleColumnVisibility,
        setColumnOption,
        resetReportItems,
        setAutoSave,
        setShowPropertyLines,
        toggleReportColumnVisibility
      }}>
      {children}
    </ProjectContext.Provider>
  );
};

const useProjectContext = () => useContext(ProjectContext);

export { ProjectProvider, useProjectContext };
